/**
 * Deterministic rules for Financial Summary takeaways, notices, and next steps.
 */

import type {
  BudgetPerformanceRow,
  ExpenseByCategoryRow,
  FinancialSummaryDataNotice,
  FinancialSummaryRuleFinding,
  MonthlyTrendRow,
  PeriodComparisonSection,
} from '../../types/report.types';
import type { OneOffIncomeAnalysis } from './financial-summary-income-analysis';
import type { FinancialSummaryTransaction } from './financial-summary-dataset';

export interface FinancialSummaryRulesInput {
  currentTransactions: FinancialSummaryTransaction[];
  priorTransactions: FinancialSummaryTransaction[];
  incomeAnalysis: OneOffIncomeAnalysis;
  totalExpenses: number;
  expenseByCategory: ExpenseByCategoryRow[];
  budgetPerformance: BudgetPerformanceRow[];
  monthlyTrend: MonthlyTrendRow[];
  periodComparison: PeriodComparisonSection;
  unknownCategoryAmount: number;
  unknownCategoryCount: number;
  uncategorisedAmount: number;
  uncategorisedCount: number;
}

const SEVERITY_RANK: Record<FinancialSummaryRuleFinding['severity'], number> = {
  high: 0,
  medium: 1,
  low: 2,
};

export function isPriorPeriodComparisonReliable(
  currentTransactions: FinancialSummaryTransaction[],
  priorTransactions: FinancialSummaryTransaction[],
  priorTotalIncome: number,
  currentTotalIncome: number
): boolean {
  if (currentTransactions.length === 0) return false;
  const countRatio = priorTransactions.length / currentTransactions.length;
  if (countRatio < 0.1) return false;
  if (currentTotalIncome > 0 && priorTotalIncome < currentTotalIncome * 0.1) return false;
  return true;
}

export function evaluateFinancialSummaryRules(
  input: FinancialSummaryRulesInput
): FinancialSummaryRuleFinding[] {
  const findings: FinancialSummaryRuleFinding[] = [];
  const {
    incomeAnalysis,
    expenseByCategory,
    budgetPerformance,
    monthlyTrend,
    periodComparison,
    currentTransactions,
    priorTransactions,
    unknownCategoryAmount,
    unknownCategoryCount,
    uncategorisedAmount,
    uncategorisedCount,
  } = input;

  const comparisonReliable = isPriorPeriodComparisonReliable(
    currentTransactions,
    priorTransactions,
    periodComparison.priorTotalIncome,
    periodComparison.currentTotalIncome
  );

  if (!comparisonReliable) {
    findings.push({
      id: 'incompletePriorPeriod',
      severity: 'high',
      message:
        'Prior period has too little data for a fair comparison. Period-over-period changes are hidden.',
      action: 'Use a longer history or full calendar/financial year once more data is available.',
    });
  }

  if (incomeAnalysis.lowIncomeMonthLabels.length > 0) {
    findings.push({
      id: 'missingIncomeMonths',
      severity: 'high',
      amount: incomeAnalysis.medianMonthlyIncome,
      message: `No or low income recorded in ${incomeAnalysis.lowIncomeMonthLabels.join(', ')}.`,
      action: 'Check all accounts are linked and income is categorised correctly.',
    });
  }

  if (unknownCategoryCount > 0 || uncategorisedCount > 0) {
    const total = unknownCategoryAmount + uncategorisedAmount;
    const count = unknownCategoryCount + uncategorisedCount;
    findings.push({
      id: 'uncategorisedTransactions',
      severity: 'medium',
      amount: total,
      message: `${count} transaction${count === 1 ? '' : 's'} (${formatAudInline(total)}) need categorisation.`,
      action: 'Review and assign categories so totals and tax views stay accurate.',
    });
  }

  if (incomeAnalysis.possibleTransferIncomeTransactions.length > 0) {
    const top = incomeAnalysis.possibleTransferIncomeTransactions.sort(
      (a, b) => b.amount - a.amount
    )[0];
    findings.push({
      id: 'possibleTransferAsIncome',
      severity: 'medium',
      amount: top.amount,
      message: `A large income entry (${formatAudInline(top.amount)}) may be a transfer rather than earnings.`,
      action: 'Recategorise transfers so income and savings rates reflect real earnings.',
    });
  }

  if (incomeAnalysis.oneOffIncome > 0) {
    findings.push({
      id: 'oneOffIncome',
      severity: 'medium',
      amount: incomeAnalysis.oneOffIncome,
      message: `One-off income of ${formatAudInline(incomeAnalysis.oneOffIncome)} affects your headline savings rate.`,
      action: 'Use underlying savings rate for day-to-day budgeting; plan how to use lump sums.',
    });
  }

  const deficitMonths = monthlyTrend.filter((m) => m.net < 0).length;
  if (deficitMonths > 0) {
    findings.push({
      id: 'deficitMonths',
      severity: 'medium',
      amount: deficitMonths,
      message: `You spent more than you earned in ${deficitMonths} of ${monthlyTrend.length} months in this period.`,
      action: 'Review high-expense months and adjust budgets or income timing.',
    });
  }

  const topCategory = expenseByCategory[0];
  if (topCategory && topCategory.percent >= 10) {
    findings.push({
      id: 'largestCategory',
      severity: 'low',
      amount: topCategory.amount,
      message: `${topCategory.categoryName} was ${topCategory.percent.toFixed(1)}% of your spending — your largest category.`,
      action: `Review ${topCategory.categoryName} transactions for savings opportunities.`,
    });
  }

  for (const row of budgetPerformance) {
    if (row.status === 'Over') {
      findings.push({
        id: 'budgetOver',
        severity: 'medium',
        amount: Math.abs(row.remaining),
        message: `${row.categoryName} is over budget (${row.percentUsed.toFixed(0)}% of allowance).`,
        action: `Adjust spending or increase the ${row.categoryName} budget limit.`,
      });
    }
  }

  const reallocation = suggestBudgetReallocation(budgetPerformance);
  if (reallocation) {
    findings.push(reallocation);
  }

  return sortFindings(findings);
}

function suggestBudgetReallocation(
  rows: BudgetPerformanceRow[]
): FinancialSummaryRuleFinding | null {
  const overs = rows.filter((r) => r.status === 'Over');
  const unders = rows.filter((r) => r.percentUsed <= 75 && r.remaining > 0);
  for (const over of overs) {
    for (const under of unders) {
      const combinedSpent = over.totalSpent + under.totalSpent;
      const combinedLimit = over.periodBudgetTotal + under.periodBudgetTotal;
      if (combinedSpent <= combinedLimit) {
        const moveAmount = Math.min(
          Math.abs(over.remaining),
          under.remaining / Math.max(1, under.averageSpentPerMonth > 0 ? 1 : 1)
        );
        const monthlyMove = Math.min(under.budget * 0.25, over.budget * 0.25, moveAmount);
        if (monthlyMove > 0) {
          return {
            id: 'budgetReallocation',
            severity: 'low',
            amount: monthlyMove,
            message: `Combined ${under.categoryName} and ${over.categoryName} budgets are within total limits; ${over.categoryName} is over while ${under.categoryName} is under.`,
            action: `Consider moving about ${formatAudInline(monthlyMove)}/month from ${under.categoryName} to ${over.categoryName}.`,
          };
        }
      }
    }
  }
  return null;
}

export function buildDataNoticesFromFindings(
  findings: FinancialSummaryRuleFinding[],
  input: FinancialSummaryRulesInput
): FinancialSummaryDataNotice[] {
  const notices: FinancialSummaryDataNotice[] = [];
  const ids = new Set(findings.map((f) => f.id));

  if (ids.has('incompletePriorPeriod')) {
    notices.push({
      id: 'incompletePriorPeriod',
      message:
        'Not enough data to compare with the prior period. Comparison values are not shown.',
    });
  }
  if (ids.has('missingIncomeMonths')) {
    notices.push({
      id: 'missingIncomeMonths',
      message: input.incomeAnalysis.lowIncomeMonthLabels.length
        ? `No/low income recorded in ${input.incomeAnalysis.lowIncomeMonthLabels.join(', ')}. Check all accounts are linked.`
        : 'Some months show little or no income. Check all accounts are linked.',
    });
  }
  if (ids.has('uncategorisedTransactions')) {
    const count = input.unknownCategoryCount + input.uncategorisedCount;
    const total = input.unknownCategoryAmount + input.uncategorisedAmount;
    notices.push({
      id: 'uncategorisedTransactions',
      message: `${count} uncategorised or unknown transaction${count === 1 ? '' : 's'} totalling ${formatAudInline(total)}.`,
    });
  }
  if (ids.has('possibleTransferAsIncome')) {
    notices.push({
      id: 'possibleTransferAsIncome',
      message:
        'One or more large credits may be transfers counted as income. Review large income entries.',
    });
  }

  return notices;
}

export function topKeyTakeaways(
  findings: FinancialSummaryRuleFinding[],
  limit = 5
): FinancialSummaryRuleFinding[] {
  return sortFindings(findings).slice(0, limit);
}

export function nextStepsFromFindings(
  findings: FinancialSummaryRuleFinding[],
  limit = 8
): FinancialSummaryRuleFinding[] {
  return sortFindings(findings)
    .filter((f) => f.action)
    .slice(0, limit);
}

function sortFindings(findings: FinancialSummaryRuleFinding[]): FinancialSummaryRuleFinding[] {
  return [...findings].sort((a, b) => {
    const sev = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    if (sev !== 0) return sev;
    return (b.amount ?? 0) - (a.amount ?? 0);
  });
}

function formatAudInline(amount: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}
